import React, { useMemo } from 'react';
import {
    Grid,
    IconButton,
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography
} from '@mui/material';
import { endOfDay } from 'date-fns';
import { useParams } from 'react-router-dom';

import {
    LocalShipping as VehicleIcon,
    Construction as EquipmentIcon,
    CardMembership as LicenseIcon,
    Forest as SustainabilityIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';

import METADATA_TYPES from '../../../types/MetadataType';

import {
    AutocompleteField,
    NumberField,
    TextField,
    DateTimeField,
    BoolField
} from '../../CommonComponents/Form';

import { SectionDivider } from '../../CommonComponents';

import { fetchGroupsByTenantId, selectGroupsByTenantId } from '../../../redux/actions/groups';
import { fetchUsersByTenantId, selectUsersByTenantId } from '../../../redux/actions/users';
import { fetchSettableMetadataForOrder, selectSettableMetadataForOrder } from '../../../redux/actions/metadata';
import { useDispatch, useSelector } from '../../../redux/hooks';
import { searchTenants } from '../../../api-admin/tenants';
import FormContext from '../../CommonComponents/Form/FormContext';
import { hasStandardProperty, isCustomCertInPresets, isInputAlreadyInList } from '../../../utils/metadata';
import OrderDate from './OrderDate';
import OrderLocation from './OrderLocation';
import { validateOrderVolumeAndLoadingMeters } from '../../CommonComponents/Form/validations/validations';

type PropsType = {
    entity: { [key: string]: any },
    onChange: (e: { [key: string]: any }) => void,
    skipRequiredValidation?: boolean
};

type CustomCertType = {
    id: string;
    value: string;
};

export default function OrderFormFields(props: PropsType) {
    const { onChange, entity, skipRequiredValidation } = props;
    const { id } = useParams();

    const dispatch = useDispatch();
    const groups = useSelector(selectGroupsByTenantId(entity.tenantId));
    const settableMetadata = useSelector(selectSettableMetadataForOrder());

    const fetchGroups = React.useCallback(() => {
        dispatch(fetchGroupsByTenantId(entity.tenantId));
    }, [dispatch, entity.tenantId]);

    const users = useSelector(selectUsersByTenantId(entity.tenantId));

    const fetchUsers = React.useCallback(() => {
        dispatch(fetchUsersByTenantId(entity.tenantId));
    }, [dispatch, entity.tenantId]);

    const formContext = React.useContext(FormContext);
    const isSubmitting = formContext?.isSubmitting;

    const fetchCarriers = React.useCallback(async (query?: string) => {
        if (!query || query.trim().length < 2) {
            return [];
        }
        const results = await searchTenants(query.trim());
        return (results || []).filter((c: { id: number, isCarrier?: boolean }) => (
            Boolean(c.isCarrier) && c.id !== entity.tenantId
        ));
    }, [entity.tenantId]);

    const volumeAndLoadingMetersValidation = useMemo(() => [validateOrderVolumeAndLoadingMeters({ volume: entity.volume, loadingMeters: entity.loadingMeters })], [entity.volume, entity.loadingMeters]);

    const [initialEntity, setInitialEntity] = React.useState<{ [key: string]: any } | undefined>(undefined);

    const [customLicencesCertificates, setCustomLicencesCertificates] = React.useState<Array<{ id: string; value: string }>>([]);

    const [hasSettableMetadata, setHasSettableMetadata] = React.useState<boolean>(false);

    const licenseCount = (
        entity.licencesCertificates?.filter((lic: string) => settableMetadata.licencesCertificates
            .includes(lic)).length ?? 0) + customLicencesCertificates.filter((clic) => clic.value.trim()).length;

    const [collapsed, setCollapsed] = React.useState<{ [key: string]: boolean }>({
        vehicleTypes: true,
        equipment: true,
        sustainability: true,
        licencesCertificates: true,
    });

    const toggleCollapsed = (key: string) => {
        setCollapsed((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    const changeStandardProperty = ({ key, array, value }: { key: string; array: string[]; value: string }) => {
        const createUpdatedArray = () => {
            if (array && array.includes(value)) {
                return array.filter((i) => i !== value);
            }
            return [...(array || []), value];
        };
        onChange({
            ...entity,
            [key]: createUpdatedArray()
        });
    };

    const addCustomLicenceCertificate: () => void = () => {
        setCustomLicencesCertificates([
            ...customLicencesCertificates,
            { id: `custom-cert-${Date.now().toString()}`, value: '' }
        ]);
    };

    const removeCustomLicenceCertificate = (certId: string) => {
        const item = customLicencesCertificates.find((i) => i.id === certId);
        const newItems = customLicencesCertificates.filter((i) => i.id !== certId);

        setCustomLicencesCertificates(newItems);
        if ((item as CustomCertType)?.value
            && (entity.customLicencesCertificates || []).includes((item as CustomCertType).value)) {
            onChange({
                ...entity,
                [METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES]: (entity.customLicencesCertificates || [])
                    .filter((lic: string) => lic !== (item as CustomCertType).value)
            });
        }
    };

    const changeCustomLicenceCertificate = ({
        certId,
        newValue
    }: {
        certId: string;
        newValue: string
    }) => {
        const item = customLicencesCertificates.find((cert) => cert.id === certId);
        const oldValue = (item as CustomCertType)?.value;
        // eslint-disable-next-line no-confusing-arrow
        const newItems = customLicencesCertificates.map((cert) => cert.id === certId
            ? { ...cert, value: newValue } : cert);
        setCustomLicencesCertificates(newItems);

        const updatedValues = entity.customLicencesCertificates || [];

        if (oldValue) {
            const oldIndex = updatedValues.indexOf(oldValue);
            if (oldIndex > -1) {
                updatedValues.splice(oldIndex, 1);
            }
        }

        if (newValue && !updatedValues.includes(newValue)) {
            updatedValues.push(newValue);
        }

        onChange({
            ...entity,
            [METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES]: updatedValues
        });
    };

    React.useEffect(() => {
        if (!initialEntity) return;
        const mappedLicCert = (initialEntity.customLicencesCertificates || [])
            .map((cert: string, idx: number) => ({ id: `init-cert-${idx}`, value: cert }));
        setCustomLicencesCertificates(mappedLicCert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialEntity]);

    React.useEffect(() => {
        if (entity !== undefined) {
            setInitialEntity(entity);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    React.useEffect(() => {
        dispatch(fetchSettableMetadataForOrder());
    }, [dispatch]);

    React.useEffect(() => {
        if (settableMetadata.vehicleTypes.length && settableMetadata.equipment.length && settableMetadata.sustainability.length && settableMetadata.licencesCertificates.length) {
            setHasSettableMetadata(true);
        }
    }, [settableMetadata]);

    return (
        <>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Upphämtningsdatum" marginTop={0} />
                    <OrderDate
                        partialKey="pickup"
                        partialName="upphämtnings"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Leveransdatum" marginTop={0} />
                    <OrderDate
                        partialKey="delivery"
                        partialName="leverans"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                        firstMinDate={entity.firstPickupDate ? entity.firstPickupDate : undefined}
                        lastMinDate={entity.lastPickupDate ? entity.lastPickupDate : undefined}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Upphämtningsplats" />
                    <OrderLocation
                        partialKey="pickup"
                        partialName="upphämtnings"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Leveranssplats" />
                    <OrderLocation
                        partialKey="delivery"
                        partialName="leverans"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Gods" />

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="grossWeight"
                                label="Totalvikt [kg]"
                                value={entity.grossWeight}
                                onChange={onChange}
                                minValue={0}
                                required={!skipRequiredValidation}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="chargeableWeight"
                                label="FDR-vikt [kg]"
                                value={entity.chargeableWeight}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="loadingMeters"
                                label="Flakmeter"
                                value={entity.loadingMeters}
                                onChange={onChange}
                                minValue={0}
                                extraValidations={volumeAndLoadingMetersValidation}

                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="volume"
                                label="Volym [m³]"
                                value={entity.volume}
                                onChange={onChange}
                                minValue={0}
                                extraValidations={volumeAndLoadingMetersValidation}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="palletPlaces"
                                label="Pallplatser"
                                value={entity.palletPlaces}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="pallets"
                                label="Pallar"
                                value={entity.pallets}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                    </Grid>
                    {hasSettableMetadata && (
                        <>
                            <SectionDivider label="Övriga krav" marginBottom={-3} />
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <SectionDivider
                                        label={(
                                            <>
                                                {`Fordonstyper (${entity.vehicleTypes?.length || 0}) `}
                                                {collapsed.vehicleTypes ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                            </>
                                        )}
                                        icon={<VehicleIcon fontSize="small" />}
                                        onClick={() => toggleCollapsed(METADATA_TYPES.VEHICLE_TYPES)}
                                        marginBottom={1}
                                        marginTop={3}
                                    />
                                    {!collapsed.vehicleTypes && (
                                        <Grid container>
                                            {settableMetadata.vehicleTypes?.map((veh) => (
                                                <React.Fragment key={veh}>
                                                    <Grid item xs={0} xl={1} />
                                                    <Grid item xs={6} sm={6} md={12} lg={6} xl={11}>
                                                        <BoolField
                                                            name={veh}
                                                            label={veh}
                                                            value={hasStandardProperty({
                                                                array: entity.vehicleTypes,
                                                                value: veh
                                                            })}
                                                            onChange={() => changeStandardProperty({
                                                                key: METADATA_TYPES.VEHICLE_TYPES,
                                                                array: entity.vehicleTypes,
                                                                value: veh
                                                            })}
                                                            sx={{ py: 0.25 }}
                                                        />
                                                    </Grid>
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <SectionDivider
                                        label={(
                                            <>
                                                {`Utrustning (${entity.equipment?.length || 0}) `}
                                                {collapsed.equipment ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                            </>
                                        )}
                                        icon={<EquipmentIcon fontSize="small" />}
                                        onClick={() => toggleCollapsed(METADATA_TYPES.EQUIPMENT)}
                                        marginBottom={1}
                                        marginTop={3}
                                    />
                                    {!collapsed.equipment && (
                                        <Grid container>
                                            {settableMetadata.equipment?.map((eq, index) => (
                                                <React.Fragment key={eq}>
                                                    <Grid item xs={0} xl={index % 2 === 0 ? 1 : 0} />
                                                    <Grid item xs={6} sm={4} md={12} lg={4} xl={index % 2 === 0 ? 5 : 6}>
                                                        <BoolField
                                                            name={eq}
                                                            label={eq}
                                                            value={hasStandardProperty({
                                                                array: entity.equipment,
                                                                value: eq
                                                            })}
                                                            onChange={() => changeStandardProperty({
                                                                key: METADATA_TYPES.EQUIPMENT,
                                                                array: entity.equipment,
                                                                value: eq
                                                            })}
                                                            sx={{ py: 0.25 }}
                                                        />
                                                    </Grid>
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <SectionDivider
                                        label={(
                                            <>
                                                {`Hållbarhet (${entity.sustainability?.length || 0}) `}
                                                {collapsed.sustainability ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                            </>
                                        )}
                                        icon={<SustainabilityIcon fontSize="small" />}
                                        onClick={() => toggleCollapsed(METADATA_TYPES.SUSTAINABILITY)}
                                        marginBottom={1}
                                        marginTop={3}
                                    />
                                    {!collapsed.sustainability && (
                                        <Grid container>
                                            {settableMetadata.sustainability?.map((sust) => (
                                                <React.Fragment key={sust}>
                                                    <Grid item xs={0} xl={1} />
                                                    <Grid item xs={6} sm={6} md={12} lg={4} xl={11}>
                                                        <BoolField
                                                            name={sust}
                                                            label={sust}
                                                            value={hasStandardProperty({
                                                                array: entity.sustainability,
                                                                value: sust
                                                            })}
                                                            onChange={() => changeStandardProperty({
                                                                key: METADATA_TYPES.SUSTAINABILITY,
                                                                array: entity.sustainability,
                                                                value: sust
                                                            })}
                                                            sx={{ py: 0.25 }}
                                                        />
                                                    </Grid>
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <SectionDivider
                                        label={(
                                            <>
                                                {`Licenser & Certifikat (${licenseCount || 0}) `}
                                                {collapsed.licencesCertificates ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                            </>
                                        )}
                                        icon={<LicenseIcon fontSize="small" />}
                                        onClick={() => toggleCollapsed(METADATA_TYPES.LICENCES_CERTIFICATES)}
                                        marginBottom={1}
                                        marginTop={3}
                                    />
                                    {!collapsed.licencesCertificates && (
                                        <>
                                            <Grid container>
                                                {settableMetadata.licencesCertificates?.map((lic, index) => (
                                                    <React.Fragment key={lic}>
                                                        <Grid item xs={0} xl={index % 2 === 0 ? 1 : 0} />
                                                        <Grid item xs={6} sm={4} md={6} lg={4} xl={index % 2 === 0 ? 5 : 6}>
                                                            <BoolField
                                                                name={lic}
                                                                label={lic}
                                                                value={hasStandardProperty({
                                                                    array: entity.licencesCertificates,
                                                                    value: lic
                                                                })}
                                                                onChange={() => changeStandardProperty({
                                                                    key: METADATA_TYPES.LICENCES_CERTIFICATES,
                                                                    array: entity.licencesCertificates,
                                                                    value: lic
                                                                })}
                                                                sx={{ py: 0.25 }}
                                                            />
                                                        </Grid>
                                                    </React.Fragment>
                                                ))}
                                            </Grid>
                                            <Grid container>
                                                {customLicencesCertificates.map((cert) => (
                                                    <React.Fragment key={cert.id}>
                                                        <Grid item xs={0} xl={1} />
                                                        <Grid item xs={12} xl={10} container spacing={1} alignItems="center">
                                                            <Grid item xs={10} xl={10}>
                                                                <TextField
                                                                    name={`${cert.id}`}
                                                                    label="Egen licens/certifikat"
                                                                    value={cert.value}
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLInputElement>
                                                                            | { [key: string]: string }) => {
                                                                            const newValue = (
                                                                                e as React.ChangeEvent<HTMLInputElement>
                                                                            ).target
                                                                                ? (
                                                                                    e as React.ChangeEvent<HTMLInputElement>
                                                                                ).target.value
                                                                                : (
                                                                                    (e as {
                                                                                        [key: string]: string
                                                                                    })[`custom-cert-${cert.id}`] ?? ''
                                                                                );
                                                                            changeCustomLicenceCertificate({
                                                                                certId: cert.id,
                                                                                newValue
                                                                            });
                                                                        }
                                                                    }
                                                                    required
                                                                    customValidations={
                                                                        [
                                                                            {
                                                                                isValid: !isInputAlreadyInList({
                                                                                    id: cert.id,
                                                                                    value: cert.value,
                                                                                    list: customLicencesCertificates
                                                                                }),
                                                                                errorText: 'Detta certifikat finns redan i listan'
                                                                            },
                                                                            {
                                                                                isValid: !isCustomCertInPresets({
                                                                                    value: cert.value,
                                                                                    presets:
                                                                                        settableMetadata.licencesCertificates
                                                                                }),
                                                                                errorText: 'Detta certifikat finns som förinställt alternativ.'
                                                                            }
                                                                        ]
                                                                    }
                                                                    disabled={cert.id.startsWith('init-')}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={2} xl={2}>
                                                                <IconButton
                                                                    onClick={() => removeCustomLicenceCertificate(cert.id)}
                                                                    color="error"
                                                                    size="small"
                                                                >
                                                                    <RemoveIcon />
                                                                </IconButton>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={0} xl={1} />
                                                    </React.Fragment>
                                                ))}
                                                <Grid item xs={0} xl={1} />
                                                <Grid item xs={12} xl={10}>
                                                    <IconButton
                                                        onClick={() => addCustomLicenceCertificate()}
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </Grid>
                                                <Grid item xs={0} xl={1} />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Publicering" />
                    <Grid container spacing={2}>
                        {!id && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        name="tenantName"
                                        label="Företag"
                                        value={entity.tenantName}
                                        onChange={onChange}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AutocompleteField<{ id: number, name: string, isAdmin: boolean }>
                                        label="Användare"
                                        helperText="Välj en användare för publicering"
                                        optionIdKey="id"
                                        fetchOptions={fetchUsers}
                                        options={users.filter(
                                            (u: { tenantIsAccepted: boolean }) => u.tenantIsAccepted
                                        )}
                                        getOptionName={(u) => u.name}
                                        getOptionDescription={(u) => `${u.isAdmin ? 'Administratör' : 'Användare'}`}
                                        onChange={(u) => onChange({ userId: u ? u.id : '' })}
                                        fetchOnlyOnce
                                        displayOption={entity.name || ''}
                                        required={!skipRequiredValidation}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} lg={6}>
                            <BoolField
                                name="showTenantName"
                                label="Visa ert företagsnamn"
                                value={entity.showTenantName}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl
                                component="fieldset"
                                variant="standard"
                                disabled={isSubmitting}
                                sx={{ width: '100%' }}
                            >
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        mb: 1,
                                        color: 'text.primary',
                                        typography: 'body2',
                                        fontWeight: 600
                                    }}
                                >
                                    Välj hur uppdraget ska publiceras
                                </FormLabel>
                                <RadioGroup
                                    name="publishAudience"
                                    value={entity.isPublic ? 'public' : 'channels'}
                                    onChange={(event) => {
                                        if (event.target.value === 'public') {
                                            onChange({
                                                isPublic: true,
                                                groups: [],
                                                recipients: []
                                            });
                                            return;
                                        }
                                        onChange({ isPublic: false });
                                    }}
                                >
                                    <FormControlLabel
                                        value="public"
                                        control={<Radio sx={{ mt: 0.25 }} />}
                                        sx={{ alignItems: 'flex-start', ml: 0, mr: 0, mb: 1 }}
                                        label={(
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="body1">
                                                    Publicera publikt
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Synligt för alla på marknaden
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                    <FormControlLabel
                                        value="channels"
                                        control={<Radio sx={{ mt: 0.25 }} />}
                                        sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
                                        label={(
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="body1">
                                                    Publicera till valda kanaler
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Grupper och/eller enskilda transportörer
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        {!entity.isPublic && (
                            <>
                                <Grid item xs={12}>
                                    <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                                        label="Grupper"
                                        helperText="Valfritt. Kan kombineras med transportörer."
                                        optionIdKey="id"
                                        fetchOptions={fetchGroups}
                                        options={groups.filter(
                                            (o: {
                                                isOwner: boolean;
                                                isAccepted: boolean;
                                                canPublish: boolean;
                                            }) => (
                                                o.isOwner
                                                || (o.isAccepted && o.canPublish))
                                        )}
                                        getOptionName={(o) => o.name}
                                        getOptionDescription={(o) => o.ownerTenantName || ''}
                                        onChange={(g) => {
                                            const next = Array.isArray(g) ? g : [];
                                            onChange({
                                                groups: next,
                                                isPublic: false
                                            });
                                        }}
                                        fetchOnlyOnce
                                        displayOption={entity.groups || []}
                                        multiple
                                        required={
                                            !skipRequiredValidation
                                            && !(entity.recipients?.length)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AutocompleteField<{ id: number, name: string, orgNumber?: string }>
                                        label="Transportörer"
                                        helperText="Valfritt. Kan kombineras med grupper."
                                        optionIdKey="id"
                                        fetchOptions={fetchCarriers}
                                        minQueryLength={3}
                                        getOptionName={(o) => o.name}
                                        getOptionDescription={(o) => o.orgNumber || ''}
                                        onChange={(r) => {
                                            const next = (Array.isArray(r) ? r : []).filter(
                                                (c: { id: number }) => c.id !== entity.tenantId
                                            );
                                            onChange({
                                                recipients: next,
                                                isPublic: false
                                            });
                                        }}
                                        displayOption={entity.recipients || []}
                                        filterOptions={(opts) => opts}
                                        multiple
                                        required={
                                            !skipRequiredValidation
                                            && !(entity.groups?.length)
                                        }
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="listPrice"
                                label="Riktpris [SEK]"
                                value={entity.listPrice}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <DateTimeField
                                name="expiresAt"
                                label="Anmäl intresse senast"
                                value={entity.expiresAt}
                                onChange={onChange}
                                minDateTime={new Date()}
                                maxDateTime={
                                    entity.lastDeliveryDate || entity.firstDeliveryDate
                                        ? endOfDay(new Date(entity.lastDeliveryDate || entity.firstDeliveryDate))
                                        : undefined
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="publicDescription"
                                label="Publik beskrivning"
                                value={entity.publicDescription}
                                onChange={onChange}
                                helperText="Visas för alla som kan se uppdraget. Ex: Krav på biltyp, villkor etc."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="privateDescription"
                                label="Privat beskrivning"
                                value={entity.privateDescription}
                                onChange={onChange}
                                helperText="Visas endast för den som tilldelas uppdraget.
                                Ex: Leveransdetaljer som portkod etc."
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
